;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"coo-webapp-2026-11-03-1793713891"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";globalThis["_sentryAssetPrefix"] = undefined;globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[],\"staticRoutes\":[]}";import * as Sentry from '@sentry/nextjs';
import { ENV, SENTRY_DSN } from '@/constants/envConfig';
import {
  dropHandledChunkErrors,
  registerEarlyChunkErrorListener,
} from '@/utils/chunkLoadErrorHandler';
import { SENTRY_IGNORE_ERRORS } from '@/utils/sentryIgnoreErrors';
import { attachNonJsonResponseContext } from '@/utils/sentryNonJsonContext';
import { installStorageFallback } from '@/utils/storageFallback';

// Register the chunk-load error listener as early as possible — before the page
// chunk executes — so that failures during hydration are caught. This file is
// injected by @sentry/nextjs into the _app entry bundle before the module body,
// making it the earliest client-side code to run in production builds (in dev it
// is appended to the entry instead). initChunkLoadErrorHandler()
// in _app's useEffect adds the router vector and handles cleanup.
// Before Sentry.init on purpose (TEAM-15273): Sentry hooks window.onerror, and
// Chromium runs that handler ahead of a window listener added after it. Sentry
// would then capture the SyntaxError as COO-WEBAPP-180 and mark it captured, so
// our chunk_load_failure report for the same error is dropped.
registerEarlyChunkErrorListener();

/**
 * @TODO investigate mechanisms to declutter Sentry if we log a lot of junk.
 * @see https://docs.sentry.io/clients/javascript/tips/
 */
Sentry.init({
  dsn: SENTRY_DSN,
  environment: ENV,
  release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.browserTracingIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
  ],
  // Lift a NonJsonResponseError's bounded diagnostic (url/status/contentType/bodySnippet) onto the
  // event when it reports via plain `captureException` (the checkout validate/submit recovery path),
  // which otherwise drops custom Error properties. Deliberately targeted instead of the app-wide
  // `extraErrorDataIntegration`, which would also ship every WretchError's full `.text`/`.json` body
  // to Sentry (TEAM-14935 review follow-up).
  // Also drop the bare "Unexpected token '<'" event for a stale chunk the chunk-load handler has
  // already reported as a named StaleChunkError with its URL (TEAM-15331).
  beforeSend: (event, hint) =>
    dropHandledChunkErrors(attachNonJsonResponseContext(event, hint), hint),
  enableLogs: true,
  tracesSampleRate: ENV === 'dev' ? 0 : 0.1,
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/prod-coo-api\.chowly\.io\/v1/,
    /^https:\/\/coo-api\.chowly\.io\/v1/,
  ],
  replaysSessionSampleRate: ENV === 'dev' ? 1.0 : 0.1,
  // record replays on all errors and exceptions, not messages
  replaysOnErrorSampleRate: 1,
  denyUrls: ['https://siteimproveanalytics.com', 'https://maps.googleapis.com'],
  // Verified third-party noise, checked alongside Sentry's defaults
  // (docs/SENTRY_NOISE_FILTERING.md).
  ignoreErrors: SENTRY_IGNORE_ERRORS,
});

// TEAM-14430: swap an in-memory Storage in for localStorage/sessionStorage when
// the browser blocks them, before _app reads either (this file runs ahead of the
// _app module body, see above). After Sentry.init so the single report has a client.
installStorageFallback();
